import React, { useContext, useRef, useEffect, useState, useMemo, Fragment, useCallback } from 'react'
import AppLayout from '../layouts/AppLayout'
import DataTable from '../components/DataTable'
import { AppContext } from '../contexts/AppContext'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { Bar } from 'react-chartjs-2'
import 'chart.js/auto'
import { DateRange } from 'react-date-range'
import { subYears, format, startOfYear, addMonths, parseISO } from 'date-fns'
import { enUS } from 'date-fns/locale'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { Listbox, Transition } from '@headlessui/react'
import { RxChevronDown } from 'react-icons/rx'
import { getCompetitionImage } from '../utils/competition'
import CompetitionSelector from '../components/CompetitionSelector'

// Falls back to a two-month window when a competition has no dates configured.
const getCompetitionRange = (competition) => ({
  startDate: competition?.startDate ? parseISO(competition.startDate) : new Date(),
  endDate: competition?.endDate ? parseISO(competition.endDate) : addMonths(new Date(), 2),
  key: 'selection',
})

const Overview = () => {
  const { getoverview, getoverviewstats, getoverviewdomain, getallteams, getteams, isLoading, getCompetitions } =
    useContext(AppContext)
  const [stats, setStats] = useState([])
  const [teams, setTeams] = useState([])
  const [team, setTeam] = useState('')
  const [domainstats, setdomainStats] = useState([])
  const [slctdCompetition, setslctdCompetition] = useState('')
  const [totaldataChart, settotaldataChart] = useState()
  const [totaldataChartOptions, settotaldataChartOptions] = useState()
  const [domaindataChart, setdomaindataChart] = useState()
  const [competitions, setCompetitions] = useState([])
  const [domaindataChartOptions, setdomaindataChartOptions] = useState()
  // `range` is the picker's live value and changes on every click; `appliedRange`
  // is the one the data is fetched for, and only advances once a start *and* an
  // end date have been picked.
  const [range, setRange] = useState([getCompetitionRange(null)])
  const [appliedRange, setAppliedRange] = useState(() => getCompetitionRange(null))
  // Controlled so the start/end step survives a re-render or a reopen.
  const [focusedRange, setFocusedRange] = useState([0, 0])
  // Only seeds the calendar on mount, so it has to be lifted to survive a reopen.
  const [shownDate, setShownDate] = useState(() => getCompetitionRange(null).startDate)
  const [showPicker, setShowPicker] = useState(false)
  const pendingRange = useRef(null)
  const pickerRef = useRef(null)
  const dateInputRef = useRef(null)

  const selectCompetition = useCallback((competition) => {
    const competitionRange = getCompetitionRange(competition)
    setslctdCompetition(competition)
    setRange([competitionRange])
    setAppliedRange(competitionRange)
    setFocusedRange([0, 0])
    setShownDate(competitionRange.startDate)
    pendingRange.current = null
  }, [])

  // DateRange fires onChange first, then onRangeFocusChange, within one batch.
  const handleRangeChange = useCallback((item) => {
    const selection = item.selection
    pendingRange.current = selection
    setRange([selection])
  }, [])

  // Step 1 means a start date was just set and an end date is still pending;
  // step 0 means the selection is complete, so that is when we refetch.
  const handleRangeFocusChange = useCallback((nextFocusedRange) => {
    setFocusedRange(nextFocusedRange)
    if (nextFocusedRange[1] === 0 && pendingRange.current) {
      setAppliedRange(pendingRange.current)
      pendingRange.current = null
    }
  }, [])

  const fetchCompetitions = useCallback(async () => {
    const response = await getCompetitions()
    setCompetitions(response)
    if (response.length > 0) {
      selectCompetition(response[0])
    }
  }, [getCompetitions, selectCompetition])

  useEffect(() => {
    fetchCompetitions()
  }, [fetchCompetitions])

  useEffect(() => {
    if (!slctdCompetition) return

    const fetchOverview = async () => {
      const response = await getoverview(slctdCompetition?.id, appliedRange)
      // setStats(response);

      const data = {
        labels: response.map((stat) => stat.name), // Assuming 'name' is the category label
        datasets: [
          {
            label: 'Total Links',
            data: response.map((stat) => stat.totallinks > 0 && stat.totallinks.toLocaleString()),
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
          },
          {
            label: 'Live Links',
            data: response.map((stat) => stat.totallivelinks),
            backgroundColor: 'rgba(15, 157, 88, 0.5)',
          },
          {
            label: 'Highlight Links',
            data: response.map((stat) => stat.totalhighlightlinks),
            backgroundColor: 'rgba(59, 53, 97, 0.5)',
          },
          {
            label: 'Google Links',
            data: response.map((stat) => stat.totalgooglelinks),
            backgroundColor: 'rgba(244, 180, 0, 0.5)',
          },
        ],
      }

      const options = {
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: false,
          },
          y: {
            stacked: false,
            beginAtZero: true,
          },
        },
      }

      settotaldataChart(data)
      settotaldataChartOptions(options)
    }

    const fetchOverviewDomain = async () => {
      const response = await getoverviewdomain(slctdCompetition?.id, appliedRange, team)
      setdomainStats(response)

      const data = {
        labels: response.map((stat) => stat.name),
        datasets: [
          {
            label: 'Total Links',
            data: response.map((stat) => stat.totallinks > 0 && stat.totallinks.toLocaleString()),
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
          },
          {
            label: 'Live Links',
            data: response.map((stat) => stat.totallivelinks > 0 && stat.totallivelinks.toLocaleString()),
            backgroundColor: 'rgba(15, 157, 88, 0.5)',
          },
          {
            label: 'Highlight Links',
            data: response.map((stat) => stat.totalhighlightlinks > 0 && stat.totalhighlightlinks.toLocaleString()),
            backgroundColor: 'rgba(59, 53, 97, 0.5)',
          },
          {
            label: 'Google Links',
            data: response.map((stat) => stat.totalgooglelinks > 0 && stat.totalgooglelinks.toLocaleString()),
            backgroundColor: 'rgba(244, 180, 0, 0.5)',
          },
        ],
      }

      const options = {
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: false,
          },
          y: {
            stacked: false,
            beginAtZero: true,
          },
        },
      }

      setdomaindataChart(data)
      setdomaindataChartOptions(options)
    }

    const fetchOverviewStats = async () => {
      const response = await getoverviewstats(slctdCompetition.id, appliedRange, team)
      setStats(response[0])
    }

    const fetchTeams = async () => {
      const response = await getteams(slctdCompetition.id)
      setTeams(response)
    }

    fetchOverviewDomain()
    fetchOverviewStats()
    fetchTeams()
    // if () {
    // }
  }, [slctdCompetition, appliedRange, team])

  const columns = React.useMemo(
    () => [
      { Header: 'Category', accessor: 'name' },
      { Header: 'Total Links', accessor: 'totallinks' },
      { Header: 'Total Live', accessor: 'totallivelinks' },
      { Header: 'Total Highlights', accessor: 'totalhighlightlinks' },
      { Header: 'Total Google/Bing', accessor: 'totalgooglelinks' },
    ],
    [],
  )

  const domaincolumns = useMemo(() => {
    let columns = [
      { Header: 'Domains', accessor: 'name' },
      { Header: 'Total Links', accessor: 'totallinks' },
    ]

    if (team !== '') {
      columns = [
        ...columns,
        { Header: 'TOTAL HOME MATCH LINKS', accessor: 'teamhomelinks' },
        { Header: 'TOTAL AWAY MATCH LINKS', accessor: 'teamawaylinks' },
      ]
    }

    columns = [
      ...columns,
      { Header: 'Total Live', accessor: 'totallivelinks' },
      { Header: 'Total Highlights', accessor: 'totalhighlightlinks' },
      { Header: 'Total Google/Bing', accessor: 'totalgooglelinks' },
    ]

    return columns
  }, [team])

  // The selected competition constrains what the date picker allows.
  const dateBounds = useMemo(
    () => ({
      minDate: slctdCompetition?.startDate
        ? parseISO(slctdCompetition.startDate)
        : startOfYear(subYears(new Date(), 1)),
      maxDate: slctdCompetition?.endDate ? parseISO(slctdCompetition.endDate) : undefined,
    }),
    [slctdCompetition],
  )

  const handleClickOutside = (event) => {
    // The toggle input lives outside the picker; let its own onClick handle it
    // instead of closing here and letting the two handlers fight.
    if (dateInputRef.current && dateInputRef.current.contains(event.target)) return
    if (pickerRef.current && !pickerRef.current.contains(event.target)) {
      setShowPicker(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const StatBox = ({ value, label, info }) => (
    <section className='!mb-0 !p-5'>
      <h6
        className='text-sm text-neutral-500 font-medium mb-1'
        dangerouslySetInnerHTML={{ __html: label }}
      ></h6>
      <h2 className='text-3xl font-bold'>{value}</h2>
      {info && (
        <span className='px-2 py-1 bg-neutral-200 text-neutral-500 rounded-sm text-xs font-medium leading-none'>
          &bull;&nbsp; {info}
        </span>
      )}
    </section>
  )

  return (
    <AppLayout pageName={'Overview'}>
      <>
        <div className='space-y-5'>
          <section>
            {/* The filters stay mounted while data loads — unmounting them tears
                down the date picker in the middle of a two-click selection. */}
            <div className='flex flex-wrap flex-col lg:flex-row justify-between'>
                <div className='inline-flex rounded-md mb-3'>
                  <CompetitionSelector
                    value={slctdCompetition}
                    competitions={competitions}
                    onChange={selectCompetition}
                  />
                </div>

                <div className='flex flex-col md:flex-row space-x-0 md:space-x-3 space-y-3 md:space-y-0'>
                  <div>
                    <div className='inline-flex'>
                      <span className='inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border rounded-e-0 border-gray-200 border-e-0 rounded-s-md'>
                        Filter by teams
                      </span>
                      <select
                        id='team'
                        name='team'
                        className='bg-white rounded-none rounded-e-lg border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-200 p-2.5 outline-none'
                        value={team}
                        onChange={(e) => setTeam(e.target.value)}
                        required
                      >
                        <option value=''>All teams</option>
                        {teams.map((team) => (
                          <option
                            key={team.id}
                            value={team.id}
                          >
                            {team.compname} - {team.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* `relative` has to sit on the picker's own parent — the
                      popup is positioned against this box, not the input row. */}
                  <div className='relative'>
                    <div className='inline-flex'>
                      <span className='inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border rounded-e-0 border-gray-200 border-e-0 rounded-s-md'>
                        Filter by dates
                      </span>
                      <input
                        className='bg-white rounded-none rounded-e-lg border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-200 p-2.5 outline-none'
                        value={`${format(range[0].startDate, 'MMM dd, yyyy', { locale: enUS })} - ${format(
                          range[0].endDate,
                          'MMM dd, yyyy',
                          { locale: enUS },
                        )}`}
                        readOnly
                        ref={dateInputRef}
                        onClick={() => setShowPicker((open) => !open)}
                      />
                    </div>
                    {showPicker && (
                      <div
                        className='absolute right-0 z-20 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg'
                        ref={pickerRef}
                      >
                        <DateRange
                          ranges={range}
                          onChange={handleRangeChange}
                          focusedRange={focusedRange}
                          onRangeFocusChange={handleRangeFocusChange}
                          shownDate={shownDate}
                          onShownDateChange={setShownDate}
                          months={2}
                          direction='horizontal'
                          locale={enUS}
                          moveRangeOnFirstSelection={false}
                          retainEndDateOnFirstSelection={false}
                          showMonthArrow={true}
                          showMonthAndYearPickers={true}
                          showSelectionPreview={true}
                          showDateDisplay={false}
                          dragSelectionEnabled={true}
                          editableDateInputs={false}
                          // Calendar re-runs calcFocusDate whenever `ranges`
                          // changes identity; without these it snaps back to the
                          // start date's month after every click.
                          preventSnapRefocus={true}
                          calendarFocus='forwards'
                          minDate={dateBounds.minDate}
                          maxDate={dateBounds.maxDate}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

            {isLoading ? (
              <div className='flex justify-center w-full py-12'>
                <Spinner
                  color='#CCCCCC'
                  size={24}
                />
              </div>
            ) : (
              <>
              <div
                className={`grid ${
                  team !== '' ? 'grid-cols-2 lg:grid-cols-7' : 'grid-cols-2 lg:grid-cols-5'
                } grid gap-5 my-3`}
              >
                <StatBox
                  value={stats.totallinks}
                  label='Total Links'
                />
                {team !== '' && (
                  <>
                    <StatBox
                      value={stats.teamhomelinks}
                      label='Total Home Matches'
                    />
                    <StatBox
                      value={stats.teamawaylinks}
                      label='Total Away Matches'
                    />
                  </>
                )}
                <StatBox
                  value={stats.totallivelinks}
                  label='Total Live'
                />
                <StatBox
                  value={stats.totalhighlightlinks}
                  label='Total Highlights'
                />
                <StatBox
                  value={stats.totalgooglelinks}
                  label='Total Google/Bing'
                />
                <StatBox
                  value={stats.totalmatchdays}
                  label='Total Matches'
                />
              </div>

              {domainstats.length > 0 && (
                <>
                  <div>
                    <DataTable
                      data={domainstats}
                      columns={domaincolumns}
                    />
                  </div>
                  <div className='h-96'>
                    <Bar
                      data={domaindataChart}
                      options={domaindataChartOptions}
                    />
                  </div>
                </>
              )}
              </>
            )}
          </section>
        </div>
      </>
    </AppLayout>
  )
}

export default Overview
